import { ChangeEvent, useCallback, useId, useMemo } from 'react';
import { accentColorsDarkMode, accentColorsLightMode } from 'apps/cdp-web-demo/app/data/themes';
import { getHexFromAccentColor } from 'apps/cdp-web-demo/app/getHexFromAccentColor';
import {
  AccentColor,
  AccentColorName,
  ConfigFormHandlers,
  ConfigState,
  HexColor,
  isHexColor,
} from 'apps/cdp-web-demo/app/types';
import { ControlGroup } from '@cbhq/cds-web/controls/ControlGroup';
import { Text } from '@cbhq/cds-web/typography';

import styles from './Color.module.css';
import { Radio as ColorRadio } from './ColorRadioInput';

type ColorOption = AccentColorName | 'customColor' | `customColor--#${string}`;

type Props = {
  accentColor: ConfigState['accentColor'];
  customColor: ConfigState['customColor'];
  setAccentColor: ConfigFormHandlers['setAccentColor'];
  themeMode: ConfigState['themeMode'];
};

function ColorField({ accentColor, customColor, themeMode, setAccentColor }: Props) {
  const labelId = useId();

  const colorOptions = useMemo<
    {
      value: ColorOption;
      'aria-label': string;
      accentColor: string;
      name: string;
    }[]
  >(() => {
    return [
      {
        value: 'blue' as const,
        name: 'accentColor',
        'aria-label': 'Blue',
        accentColor: themeMode === 'light' ? accentColorsLightMode.blue : accentColorsDarkMode.blue,
      },
      {
        value: 'green' as const,
        name: 'accentColor',
        'aria-label': 'Green',
        accentColor:
          themeMode === 'light' ? accentColorsLightMode.green : accentColorsDarkMode.green,
      },
      {
        value: 'orange' as const,
        name: 'accentColor',
        'aria-label': 'Orange',
        accentColor:
          themeMode === 'light' ? accentColorsLightMode.orange : accentColorsDarkMode.orange,
      },
      {
        value: 'pink' as const,
        name: 'accentColor',
        'aria-label': 'Pink',
        accentColor: themeMode === 'light' ? accentColorsLightMode.pink : accentColorsDarkMode.pink,
      },
      {
        value: 'teal' as const,
        name: 'accentColor',
        'aria-label': 'Teal',
        accentColor: themeMode === 'light' ? accentColorsLightMode.teal : accentColorsDarkMode.teal,
      },
      {
        value: 'customColor' as const,
        name: 'accentColor',
        'aria-label': 'Custom Color',
        accentColor: customColor ?? (isHexColor(accentColor) ? accentColor : ''),
      },
    ];
  }, [themeMode, accentColor, customColor]);

  const handleAccentColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let hex: HexColor | undefined;
      const fallbackColor = getHexFromAccentColor('blue', themeMode);
      if (e.target.value.startsWith('customColor')) {
        hex = e.target.value.startsWith('customColor--')
          ? (e.target.value.split('--')[1] as HexColor)
          : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            customColor || undefined;

        setAccentColor(hex ?? fallbackColor, hex);
      } else {
        hex = getHexFromAccentColor(e.target.value, themeMode);
        setAccentColor(e.target.value as AccentColor);
      }
    },
    [setAccentColor, customColor, themeMode],
  );

  return (
    <ControlGroup
      label={
        <Text id={labelId} font="label1" className={styles['radio-label']}>
          Color
        </Text>
      }
      aria-labelledby={labelId}
      columnGap={2}
      ControlComponent={ColorRadio}
      value={accentColor.startsWith('#') ? 'customColor' : accentColor}
      role="radiogroup"
      onChange={handleAccentColorChange}
      options={colorOptions}
      className={styles['radio-group']}
    />
  );
}

export { ColorField };
